package com.kanati.admin.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.kanati.admin.app.AdminRoute
import com.kanati.admin.app.AdminRouteScope
import com.kanati.admin.state.DistrictContextState
import com.kanati.admin.state.MaterialThemePalette
import com.kanati.admin.state.SelectedDistrictContext
import com.kanati.admin.state.SelectedSchoolContext
import com.kanati.admin.state.ThemeMode
import com.kanati.admin.state.formatTaxRatePercent
import com.kanati.shared.admin.Operator
import com.kanati.shared.admin.OperatorRole
import com.kanati.shared.school.School
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontFamily
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text

@Composable
fun AppShell(
    activeRoute: AdminRoute,
    availableRoutes: List<AdminRoute>,
    availableSchools: List<School>,
    currentOperator: Operator,
    districtAdminAccess: Boolean,
    schoolAdminAccess: Boolean,
    districtContext: DistrictContextState,
    currentDistrict: SelectedDistrictContext?,
    currentSchool: SelectedSchoolContext?,
    theme: MaterialThemePalette,
    themeMode: ThemeMode,
    onDistrictSelected: (String) -> Unit,
    onSchoolSelected: (String) -> Unit,
    onReloadContext: () -> Unit,
    onRouteSelected: (AdminRoute) -> Unit,
    onToggleTheme: () -> Unit,
    onLogout: () -> Unit,
    isRouteTransitioning: Boolean,
    content: @Composable () -> Unit,
) {
    val routeBarWidth = "${availableRoutes.coerceAtLeastOne().size * 120}px"
    val roleLabel = when {
        currentOperator.role == OperatorRole.SUPERADMIN -> "Superadmin"
        districtAdminAccess -> "District Admin"
        schoolAdminAccess -> "School Admin"
        else -> "Operator"
    }

    Div(
        attrs = {
            style {
                fontFamily("\"SF Pro Text\", \"SF Pro Display\", \"Avenir Next\", \"Segoe UI\", sans-serif")
                padding(24.px)
                property("min-height", "100vh")
                property(
                    "background",
                    "linear-gradient(180deg, ${theme.background} 0%, ${theme.surfaceContainerLow} 55%, ${theme.surfaceContainerHigh} 100%)"
                )
                property("color", theme.onBackground)
                property("position", "relative")
                property("overflow", "hidden")
            }
        }
    ) {
        BackgroundOrb(size = "280px", top = "-90px", left = "-80px", fill = theme.primaryContainer)
        BackgroundOrb(size = "240px", top = "240px", right = "-60px", fill = theme.secondaryContainer)

        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(18.px)
                    property("max-width", "1320px")
                    property("margin", "0 auto")
                    property("position", "relative")
                    property("z-index", "1")
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        padding(22.px)
                        borderRadius(28.px)
                        property(
                            "background",
                            "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surface} 100%)"
                        )
                        property("border", "1px solid ${theme.outlineVariant}")
                        property("box-shadow", "0 20px 56px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
                        property("backdrop-filter", "blur(24px) saturate(160%)")
                        property("position", "relative")
                        property("overflow", "hidden")
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            property("position", "absolute")
                            property("top", "0")
                            property("left", "0")
                            property("right", "0")
                            property("height", "84px")
                            property(
                                "background",
                                "linear-gradient(180deg, ${theme.scrim} 0%, ${theme.surfaceTint} 40%, transparent 100%)"
                            )
                            property("pointer-events", "none")
                        }
                    }
                )

                Div(
                    attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(18.px)
                            property("position", "relative")
                            property("z-index", "1")
                        }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                property("justify-content", "space-between")
                                property("align-items", "start")
                                property("gap", "16px")
                                property("flex-wrap", "wrap")
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    gap(8.px)
                                }
                            }
                        ) {
                            P(
                                attrs = {
                                    style {
                                        margin(0.px)
                                        property("color", theme.onSurfaceVariant)
                                        property("font-size", "0.78rem")
                                        property("font-weight", "700")
                                        property("letter-spacing", "0.12em")
                                        property("text-transform", "uppercase")
                                    }
                                }
                            ) {
                                Text("District + School Admin")
                            }

                            H1(
                                attrs = {
                                    style {
                                        margin(0.px)
                                        property("font-family", "\"SF Pro Display\", \"Avenir Next\", sans-serif")
                                        property("font-size", "2rem")
                                        property("line-height", "1")
                                        property("letter-spacing", "-0.05em")
                                    }
                                }
                            ) {
                                Text("Kanati Admin")
                            }

                            P(
                                attrs = {
                                    style {
                                        margin(0.px)
                                        property("color", theme.onSurfaceVariant)
                                        property("font-size", "0.94rem")
                                        property("line-height", "1.5")
                                    }
                                }
                            ) {
                                Text("District settings stay district-scoped. Products, inventory, events, loyalty, and reports run in an intentional school context.")
                            }
                        }

                        Div(
                            attrs = {
                                style {
                                    display(DisplayStyle.Flex)
                                    property("align-items", "center")
                                    property("gap", "10px")
                                    property("flex-wrap", "wrap")
                                }
                            }
                        ) {
                            OperatorChip(
                                displayName = currentOperator.displayName,
                                roleLabel = roleLabel,
                                theme = theme,
                            )

                            ActionButton(
                                label = if (themeMode == ThemeMode.Light) "Dark" else "Light",
                                onClick = onToggleTheme,
                                theme = theme,
                                isPrimary = false,
                            )

                            ActionButton(
                                label = "Sign Out",
                                onClick = onLogout,
                                theme = theme,
                                isPrimary = false,
                            )
                        }
                    }

                    Div(
                        attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                property("align-items", "start")
                                property("gap", "14px")
                                property("flex-wrap", "wrap")
                            }
                        }
                    ) {
                        if (districtContext.districts.size > 1) {
                            SelectorPanel(
                                label = "District",
                                currentValue = districtContext.selectedDistrictId,
                                emptyLabel = when {
                                    districtContext.isLoading -> "Loading districts..."
                                    districtContext.districts.isEmpty() -> "No districts available"
                                    else -> "Select a district"
                                },
                                options = districtContext.districts.map {
                                    it.id to "${it.name} - ${it.city}, ${it.state.name}"
                                },
                                theme = theme,
                                isDisabled = districtContext.isLoading || districtContext.districts.isEmpty(),
                                onValueSelected = onDistrictSelected,
                            )
                        }

                        if (availableSchools.isNotEmpty()) {
                            SelectorPanel(
                                label = "School",
                                currentValue = districtContext.selectedSchoolId,
                                emptyLabel = when {
                                    districtContext.isLoading -> "Loading schools..."
                                    availableSchools.size == 1 -> availableSchools.single().name
                                    else -> "Select a school"
                                },
                                options = availableSchools.map { school ->
                                    school.id to (school.shortName?.takeIf(String::isNotBlank)?.let { "${school.name} ($it)" } ?: school.name)
                                },
                                theme = theme,
                                isDisabled = districtContext.isLoading || availableSchools.isEmpty(),
                                onValueSelected = onSchoolSelected,
                            )
                        }

                        ContextPanel(
                            label = "Current District",
                            value = currentDistrict?.name ?: "No district selected",
                            supportingValue = currentDistrict?.detail ?: "Required before district-level screens can load",
                            facts = currentDistrict?.let(::districtFacts).orEmpty(),
                            theme = theme,
                            onReloadContext = onReloadContext,
                            isReloading = districtContext.isLoading,
                        )

                        if (availableSchools.isNotEmpty()) {
                            ContextPanel(
                                label = "Current School",
                                value = currentSchool?.name ?: "No school selected",
                                supportingValue = currentSchool?.shortName ?: "Required before school-scoped operational screens can load",
                                facts = currentSchool?.let(::schoolFacts).orEmpty(),
                                theme = theme,
                                onReloadContext = onReloadContext,
                                isReloading = districtContext.isLoading,
                            )
                        }
                    }

                    districtBanner(
                        districtContext = districtContext,
                        currentDistrict = currentDistrict,
                        currentSchool = currentSchool,
                        activeRoute = activeRoute,
                        currentOperator = currentOperator,
                        theme = theme,
                    )
                }
            }

            Div(
                attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        property("justify-content", "flex-start")
                        property("overflow-x", "auto")
                        property("padding-bottom", "4px")
                        property("scrollbar-width", "thin")
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            property("position", "relative")
                            property("width", "min(100%, $routeBarWidth)")
                            property("min-width", routeBarWidth)
                            property("flex-shrink", "0")
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "repeat(${availableRoutes.coerceAtLeastOne().size}, minmax(0, 1fr))")
                            property("gap", "6px")
                            padding(6.px)
                            borderRadius(999.px)
                            property("background", theme.surfaceContainer)
                            property("border", "1px solid ${theme.outlineVariant}")
                            property("box-shadow", "0 12px 28px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
                            property("backdrop-filter", "blur(18px) saturate(160%)")
                            property("overflow", "hidden")
                        }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                property("position", "absolute")
                                property("top", "6px")
                                property("bottom", "6px")
                                property("left", routeIndicatorLeft(activeRoute, availableRoutes.coerceAtLeastOne()))
                                property("width", "calc((100% - ${(availableRoutes.coerceAtLeastOne().size + 1) * 6}px) / ${availableRoutes.coerceAtLeastOne().size})")
                                borderRadius(999.px)
                                property(
                                    "background",
                                    "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.primaryContainer} 100%)"
                                )
                                property("border", "1px solid ${theme.outlineVariant}")
                                property("box-shadow", "0 10px 20px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
                                property("transition", "left 240ms cubic-bezier(0.22, 1, 0.36, 1)")
                                property("will-change", "left")
                            }
                        }
                    )

                    availableRoutes.coerceAtLeastOne().forEach { route ->
                        RouteTabButton(
                            route = route,
                            activeRoute = activeRoute,
                            onRouteSelected = onRouteSelected,
                            theme = theme,
                        )
                    }
                }
            }

            Div(
                attrs = {
                    style {
                        property("position", "relative")
                        property("min-height", "420px")
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(18.px)
                            property("position", "relative")
                            property("z-index", "1")
                            property("opacity", if (isRouteTransitioning) "0.18" else "1")
                            property("transform", if (isRouteTransitioning) "translateY(10px)" else "translateY(0)")
                            property("filter", if (isRouteTransitioning) "blur(2px)" else "none")
                            property("transition", "opacity 180ms ease, transform 180ms ease, filter 180ms ease")
                            property("pointer-events", if (isRouteTransitioning) "none" else "auto")
                        }
                    }
                ) {
                    content()
                }

                if (isRouteTransitioning) {
                    RouteTransitionShimmer(theme = theme)
                }
            }
        }
    }
}

@Composable
private fun SelectorPanel(
    label: String,
    currentValue: String?,
    emptyLabel: String,
    options: List<Pair<String, String>>,
    theme: MaterialThemePalette,
    isDisabled: Boolean,
    onValueSelected: (String) -> Unit,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(6.px)
                flex(1)
                property("min-width", "320px")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("color", theme.onSurfaceVariant)
                    property("font-size", "0.76rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.11em")
                    property("text-transform", "uppercase")
                }
            }
        ) {
            Text(label)
        }

        Select(
            attrs = {
                onChange { onValueSelected(it.value ?: "") }
                if (isDisabled) disabled()
                style {
                    padding(14.px, 16.px)
                    borderRadius(18.px)
                    property("box-sizing", "border-box")
                    property("width", "100%")
                    property(
                        "background",
                        "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceVariant} 100%)"
                    )
                    property("color", theme.onSurface)
                    property("border", "1px solid ${theme.outline}")
                    property("box-shadow", "inset 0 1px 0 ${theme.scrim}, 0 10px 20px ${theme.shadow}")
                    property("font-size", "0.98rem")
                    property("font-weight", "550")
                }
            }
        ) {
            Option(
                value = "",
                attrs = {
                    if (currentValue == null) selected()
                },
            ) {
                Text(emptyLabel)
            }

            options.forEach { (value, labelValue) ->
                Option(
                    value = value,
                    attrs = {
                        if (value == currentValue) selected()
                    },
                ) {
                    Text(labelValue)
                }
            }
        }
    }
}

@Composable
private fun RouteTabButton(
    route: AdminRoute,
    activeRoute: AdminRoute,
    onRouteSelected: (AdminRoute) -> Unit,
    theme: MaterialThemePalette,
) {
    var isPressed by remember(route) { mutableStateOf(false) }
    val isActive = route == activeRoute

    Button(
        attrs = {
            onClick { onRouteSelected(route) }
            onMouseDown { isPressed = true }
            onMouseUp { isPressed = false }
            onMouseLeave { isPressed = false }
            onTouchStart { isPressed = true }
            onTouchEnd { isPressed = false }
            onTouchCancel { isPressed = false }
            onBlur { isPressed = false }
            style {
                padding(12.px, 18.px)
                borderRadius(999.px)
                property("background", "transparent")
                property("color", if (isActive) theme.onPrimaryContainer else theme.onSurfaceVariant)
                property("border", "1px solid transparent")
                property("font-size", "0.95rem")
                property("font-weight", if (isActive) "700" else "600")
                property("white-space", "nowrap")
                property("position", "relative")
                property("z-index", "1")
                property(
                    "box-shadow",
                    when {
                        isPressed && isActive -> "0 3px 10px ${theme.shadow}, inset 0 2px 4px ${theme.scrim}"
                        isActive -> "none"
                        isPressed -> "inset 0 1px 3px ${theme.shadow}"
                        else -> "none"
                    }
                )
                property("cursor", "pointer")
                property(
                    "transform",
                    if (isPressed) "translateY(2px) scale(0.985)" else "translateY(0) scale(1)"
                )
                property(
                    "filter",
                    if (isPressed) "saturate(0.97) brightness(0.98)" else "saturate(1)"
                )
                property("transition", "transform 120ms ease, box-shadow 120ms ease, filter 120ms ease")
                property("will-change", "transform, box-shadow, filter")
                property("touch-action", "manipulation")
                property("user-select", "none")
                property("-webkit-tap-highlight-color", "transparent")
            }
        }
    ) {
        Text(route.label)
    }
}

private fun routeIndicatorLeft(
    activeRoute: AdminRoute,
    availableRoutes: List<AdminRoute>,
): String {
    val routeIndex = availableRoutes.indexOf(activeRoute)
    if (routeIndex <= 0) {
        return "6px"
    }
    return "calc(6px + ${routeIndex} * ((100% - ${(availableRoutes.size + 1) * 6}px) / ${availableRoutes.size} + 6px))"
}

@Composable
private fun RouteTransitionShimmer(theme: MaterialThemePalette) {
    Div(
        attrs = {
            style {
                property("position", "absolute")
                property("inset", "0")
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "18px")
                property("z-index", "2")
                property("pointer-events", "auto")
            }
        }
    ) {
        ShimmerCard(theme = theme, blockHeights = listOf("28px", "16px", "16px", "72px"))
        ShimmerCard(theme = theme, blockHeights = listOf("24px", "16px", "54px", "54px", "54px"))
    }
}

@Composable
private fun ShimmerCard(
    theme: MaterialThemePalette,
    blockHeights: List<String>,
) {
    var shimmerForward by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        while (true) {
            shimmerForward = !shimmerForward
            delay(900)
        }
    }

    Div(
        attrs = {
            style {
                borderRadius(30.px)
                padding(24.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(16.px)
                property(
                    "background",
                    "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surface} 100%)"
                )
                property("border", "1px solid ${theme.outlineVariant}")
                property("box-shadow", "0 22px 56px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
                property("backdrop-filter", "blur(26px) saturate(170%)")
            }
        }
    ) {
        blockHeights.forEachIndexed { index, height ->
            Div(
                attrs = {
                    style {
                        property("height", height)
                        borderRadius(if (index == 0) 18.px else 16.px)
                        property("width", when (index) {
                            0 -> "34%"
                            1 -> "58%"
                            else -> "100%"
                        })
                        property(
                            "background",
                            "linear-gradient(90deg, ${theme.surfaceContainer} 0%, ${theme.surfaceContainerHighest} 46%, ${theme.surfaceContainer} 100%)"
                        )
                        property("background-size", "220% 100%")
                        property("background-position", if (shimmerForward) "-120% 0" else "120% 0")
                        property("border", "1px solid ${theme.outlineVariant}")
                        property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
                        property("opacity", if (shimmerForward) "0.92" else "0.72")
                        property("transition", "background-position 900ms ease-in-out, opacity 900ms ease-in-out")
                    }
                }
            )
        }
    }
}

@Composable
private fun districtBanner(
    districtContext: DistrictContextState,
    currentDistrict: SelectedDistrictContext?,
    currentSchool: SelectedSchoolContext?,
    activeRoute: AdminRoute,
    currentOperator: Operator,
    theme: MaterialThemePalette,
) {
    when {
        districtContext.error != null -> StatusBanner(
            message = districtContext.error,
            theme = theme,
            tone = BannerTone.Error,
        )

        districtContext.isLoading -> StatusBanner(
            message = "Loading operator context for ${currentOperator.displayName}...",
            theme = theme,
        )

        districtContext.districts.isEmpty() -> StatusBanner(
            message = if (currentOperator.role == OperatorRole.SUPERADMIN) {
                "No districts are configured yet. Create one below to start platform setup."
            } else {
                "No district or school access is assigned to ${currentOperator.displayName} yet."
            },
            theme = theme,
        )

        currentDistrict == null -> StatusBanner(
            message = if (currentOperator.role == OperatorRole.SUPERADMIN) {
                "Select a district or create a new one below to continue platform setup."
            } else {
                "Select one of your allowed districts to continue."
            },
            theme = theme,
        )

        currentOperator.role == OperatorRole.SUPERADMIN && activeRoute == AdminRoute.Dashboard -> Unit

        activeRoute.scope == AdminRouteScope.SCHOOL && currentSchool == null -> StatusBanner(
            message = "Select a school before opening school-scoped operational screens.",
            theme = theme,
        )
    }
}

@Composable
private fun BackgroundOrb(
    size: String,
    fill: String,
    top: String? = null,
    right: String? = null,
    bottom: String? = null,
    left: String? = null,
) {
    Div(
        attrs = {
            style {
                property("position", "fixed")
                property("width", size)
                property("height", size)
                property("border-radius", "999px")
                property("background", fill)
                property("filter", "blur(42px)")
                property("opacity", "0.58")
                property("pointer-events", "none")
                property("z-index", "0")
                top?.let { property("top", it) }
                right?.let { property("right", it) }
                bottom?.let { property("bottom", it) }
                left?.let { property("left", it) }
            }
        }
    )
}

@Composable
private fun OperatorChip(
    displayName: String,
    roleLabel: String,
    theme: MaterialThemePalette,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(3.px)
                padding(10.px, 14.px)
                borderRadius(18.px)
                property("background", theme.secondaryContainer)
                property("color", theme.onSecondaryContainer)
                property("border", "1px solid ${theme.outlineVariant}")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.7rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.1em")
                    property("text-transform", "uppercase")
                    property("opacity", "0.8")
                }
            }
        ) {
            Text(roleLabel)
        }
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.84rem")
                    property("font-weight", "600")
                }
            }
        ) {
            Text(displayName)
        }
    }
}

@Composable
private fun ContextPanel(
    label: String,
    value: String,
    supportingValue: String,
    facts: List<Pair<String, String>>,
    theme: MaterialThemePalette,
    onReloadContext: () -> Unit,
    isReloading: Boolean,
) {
    var isReloadPressed by remember { mutableStateOf(false) }

    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(4.px)
                padding(12.px, 14.px)
                borderRadius(18.px)
                property("min-width", "220px")
                property("background", theme.surfaceContainer)
                property("border", "1px solid ${theme.outlineVariant}")
                property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    property("justify-content", "space-between")
                    property("align-items", "center")
                    property("gap", "8px")
                }
            }
        ) {
            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("color", theme.onSurfaceVariant)
                        property("font-size", "0.72rem")
                        property("font-weight", "700")
                        property("letter-spacing", "0.1em")
                        property("text-transform", "uppercase")
                    }
                }
            ) {
                Text(label)
            }

            Button(
                attrs = {
                    attr("title", if (isReloading) "Loading..." else "Reload context")
                    attr("aria-label", if (isReloading) "Loading context" else "Reload context")
                    onClick { if (!isReloading) onReloadContext() }
                    onMouseDown { if (!isReloading) isReloadPressed = true }
                    onMouseUp { isReloadPressed = false }
                    onMouseLeave { isReloadPressed = false }
                    style {
                        padding(4.px, 8.px)
                        borderRadius(10.px)
                        property("background", "transparent")
                        property("border", "none")
                        property("color", theme.onSurfaceVariant)
                        property("font-size", "0.72rem")
                        property("font-weight", "600")
                        property("cursor", if (isReloading) "default" else "pointer")
                        property("opacity", if (isReloading) "0.45" else if (isReloadPressed) "0.7" else "0.6")
                        property("transition", "opacity 120ms ease")
                        property("touch-action", "manipulation")
                        property("user-select", "none")
                    }
                }
            ) {
                Text(if (isReloading) "↻ Loading" else "↻ Reload")
            }
        }

        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.92rem")
                    property("font-weight", "650")
                }
            }
        ) {
            Text(value)
        }
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.84rem")
                    property("color", theme.onSurfaceVariant)
                    property("line-height", "1.45")
                }
            }
        ) {
            Text(supportingValue)
        }

        if (facts.isNotEmpty()) {
            Div(
                attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        property("flex-wrap", "wrap")
                        gap(8.px)
                        property("margin-top", "4px")
                    }
                }
            ) {
                facts.forEach { (label, value) ->
                    ContextFactChip(label = label, value = value, theme = theme)
                }
            }
        }
    }
}

private fun districtFacts(district: SelectedDistrictContext): List<Pair<String, String>> =
    listOfNotNull(
        district.city?.let { "City" to it },
        district.state?.let { "State" to it },
        district.county?.let { "County" to it },
        district.timezone?.let { "Timezone" to it },
        district.defaultTaxRateBps?.let { "Sales Tax" to formatTaxRatePercent(it) },
    )

private fun schoolFacts(school: SelectedSchoolContext): List<Pair<String, String>> =
    listOfNotNull(
        school.shortName?.takeIf(String::isNotBlank)?.let { "Short Name" to it },
        "Status" to if (school.active) "Active" else "Inactive",
    )

@Composable
private fun ContextFactChip(
    label: String,
    value: String,
    theme: MaterialThemePalette,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                property("align-items", "center")
                gap(6.px)
                padding(8.px, 10.px)
                borderRadius(14.px)
                property("background", theme.surfaceVariant)
                property("border", "1px solid ${theme.outlineVariant}")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.7rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                    property("color", theme.onSurfaceVariant)
                }
            }
        ) {
            Text(label)
        }
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.82rem")
                    property("font-weight", "600")
                    property("color", theme.onSurface)
                }
            }
        ) {
            Text(value)
        }
    }
}

private fun List<AdminRoute>.coerceAtLeastOne(): List<AdminRoute> =
    if (isEmpty()) listOf(AdminRoute.Settings) else this
